/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { baseComponentManager } from '../../registry/Components';
import { defaultValue } from './CommentActionMenuItems';

const CommentActionMenuItemsManager: ComponentManagerFn<
  'CommentActionMenuItems'
> = (platform: Platform) => {
  return {
    ...baseComponentManager<'CommentActionMenuItems'>(
      platform,
      'CommentActionMenuItems',
    ),
    type: 'CommentActionMenuItems',
    name: 'Comment Action Menu Items',
    isChildAllowed: (page, item, childType) =>
      (['MenuItem', 'MenuItemDivider'] as ItemType[]).includes(childType),
    defaultValue,
    props: {},
    category: 'Data',
    showEmptyPlaceholderInDesignerView: false,
    noOverlay: true,
    icon: { iconName: 'list-alt' },
    description: `**Overview**<br/>
The Comment Action Menu Items component is a non-visual container used to extend the functionality of the standard "Comments" system. While the Comments component handles the display and creation of threads automatically, this component allows you to inject custom options into the "More Actions" (kebab/ellipsis) menu of each individual comment.
Use this component to add specialized workflows—like "Mark as Solution," "Flag for Review," or "Convert to Task"—directly into the comment interface.

**1. Integration & Purpose**<br/>
This component acts as a bridge between the comment UI and your custom logic.
* **Placement:** It does not render its own UI. Instead, it must be placed within the context of a Comments component structure.
* **Behavior:** Any child components added here are automatically appended to the standard action menu (⋮) that appears on every comment.

**2. Content & Structure**<br/>
Since this component mimics a standard dropdown menu, it only accepts specific child types designed for menu construction.
* **Allowed Children:**
    * **Menu Item:** Represents a clickable action (e.g., "Mark as Resolved").
    * **Menu Item Divider:** A visual separator line used to group related actions.

**3. Contextual Data Access**<br/>
The most powerful feature of this component is its ability to access the data of the specific comment the user interacted with. When a custom menu item is clicked, the system injects the comment's metadata into the execution context.
You can access this data using expressions in your Menu Item actions:
* **$._.comment.commentUid:** The unique ID of the comment. Essential for database updates.
* **$._.comment.commentText:** The actual text content of the message.
* **$._.comment.commentedBy:** The user ID or name of the person who wrote the comment.
* **Example Usage:** To create a "Flag Comment" action, you would bind the OnClick event of a Menu Item to a workflow that uses \`$._.comment.commentUid\` to update a "FlaggedComments" data store.

**4. Design Experience**<br/>
* **Invisible Container:** In the page designer, this component does not show an empty placeholder or overlay to avoid cluttering the view. It exists purely in the component tree to manage its children.`,
  };
};

export default CommentActionMenuItemsManager;
